import { Backdrop, TableCell, TableRow, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Loading } from "shared-ui/global/Loading";
import { DiningStationTemplateRow } from "apps/web/src/views/DiningStationTemplates/DiningStationTemplateRow";
import { useState } from "react";
import ConfirmationDialog from "../../../../componentLibrary/ConfirmationDialog/ConfirmationDialog";
import { getMenuItemAppearanceGroupState } from "../../../../components/MenuItemAppearance/reducer";
import TablePage from "../../../../components/universal/TablePage";
import TablePageDefaultHeader from "../../../../components/universal/TablePage/DefaultHeader";
import {
  DiningStationTemplateTableFragment,
  useCreateDiningStationTemplateMutation,
  useDeleteDiningStationTemplateMutation,
  useDiningStationTemplateDialogQuery,
  useDiningStationTemplatesTableQuery,
  useEditDiningStationTemplateMutation
} from "../../../../types";
import { useOffsetPagination } from "../../../../utils/pagination";
import Dialog from "../../../../views/DiningStationTemplates/Dialog";
import { DiningStationTemplateModalState, getNewDiningStationTemplateModalState } from "../../../../views/DiningStationTemplates/reducer";
import { getCreateDiningStationTemplateInput, getEditDiningStationTemplateInput } from "../../../../views/DiningStationTemplates/utils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      cursor: "pointer",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
    },
    actionsCellDiv: {
      display: "flex",
    },
    missingTooltip: {
      maxWidth: 150,
    },
    warningIcon: {
      color: theme.palette.warning.light,
    },
    popoverButtonIcon: {
      marginLeft: theme.spacing(),
    },
  })
);

export const DiningStationTemplatesPage = () => {
  const classes = useStyles();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [diningStationTemplateIdForDialog, setDiningStationTemplateIdForDialog] = useState<string | null>(null);
  const [openDiningStationTemplateForDuplicateDialog, setOpenDiningStationTemplateForDuplicateDialog] =
    useState<boolean>(false);
  const [diningStationTemplateForDeleteDialog, setDiningStationTemplateForDeleteDialog] =
    useState<DiningStationTemplateTableFragment | null>(null);

  const paginationHooks = useOffsetPagination();
  const { query, limit, offset, queryText, onChangeQueryText } = paginationHooks;

  const {
    data: tableData,
    refetch,
    loading: tableLoading,
  } = useDiningStationTemplatesTableQuery({
    variables: { query, input: { offset, limit } },
    fetchPolicy: "network-only",
  });

  const { data: dialogData, loading: dialogLoading } = useDiningStationTemplateDialogQuery({
    skip: diningStationTemplateIdForDialog === null,
    variables: {
      id: diningStationTemplateIdForDialog || "",
    },
  });

  const [createDiningStationTemplate, { loading: savingCreate }] = useCreateDiningStationTemplateMutation({
    onCompleted: async () => {
      await refetch();
      closeDialog();
    },
  });

  const [editDiningStationTemplate, { loading: savingEdit }] = useEditDiningStationTemplateMutation({
    onCompleted: async () => {
      await refetch();
      closeDialog();
    },
  });

  const saving = savingCreate || savingEdit;

  const [deleteDiningStationTemplate] = useDeleteDiningStationTemplateMutation({
    onCompleted: () => {
      refetch();
      closeDialog();
    },
  });

  const handleDelete = (diningStationTemplateId: string) => {
    deleteDiningStationTemplate({
      variables: {
        input: {
          diningStationTemplateId,
        },
      },
    });
  };

  const handleSave = (state: DiningStationTemplateModalState) => {
    if (state.id === null) {
      createDiningStationTemplate({
        variables: { input: getCreateDiningStationTemplateInput(state) },
      });
    } else {
      editDiningStationTemplate({
        variables: {
          input: getEditDiningStationTemplateInput(state, state.id),
        },
      });
    }
  };

  const closeDialog = () => {
    setCreateDialogOpen(false);
    setDiningStationTemplateIdForDialog(null);
    setDiningStationTemplateForDeleteDialog(null);
    setOpenDiningStationTemplateForDuplicateDialog(false);
  };

  return (
    <>
      <TablePage
        header={
          <TablePageDefaultHeader
            title="Dining Station Template"
            queryText={queryText}
            onChangeQueryText={onChangeQueryText}
            onCreate={() => setCreateDialogOpen(true)}
          />
        }
        tableHeaderRow={
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Meals</TableCell>
            <TableCell align="left">Number of Menu Items</TableCell>
            <TableCell padding="checkbox">Actions</TableCell>
          </TableRow>
        }
        tableBodyRows={
          !tableData
            ? null
            : tableData.diningStationTemplateOffsetConnection.edges.map(row => (
                <DiningStationTemplateRow
                  key={row.id}
                  row={row}
                  onEdit={id => setDiningStationTemplateIdForDialog(id)}
                  onDelete={row => setDiningStationTemplateForDeleteDialog(row)}
                  onDuplicate={id => {
                    setDiningStationTemplateIdForDialog(id);
                    setOpenDiningStationTemplateForDuplicateDialog(true);
                  }}
                />
              ))
        }
        paginationHooks={paginationHooks}
        loading={tableLoading}
        total={tableData?.diningStationTemplateOffsetConnection.pageInfo.total || 0}
      />
      {dialogLoading && (
        <Backdrop open={dialogLoading} className={classes.backdrop}>
          <Loading progressSize="lg" />
        </Backdrop>
      )}
      {dialogData && !dialogLoading && (
        <Dialog
          open={Boolean(dialogData && !dialogLoading)}
          onClose={closeDialog}
          onSave={handleSave}
          type={openDiningStationTemplateForDuplicateDialog ? "duplicate" : "edit"}
          saving={saving}
          initialDiningStationTemplate={{
            ...dialogData.diningStationTemplate,
            name: openDiningStationTemplateForDuplicateDialog ? `[Copy of] ${dialogData.diningStationTemplate.name}` : dialogData.diningStationTemplate.name,
            id: openDiningStationTemplateForDuplicateDialog ? null : dialogData.diningStationTemplate.id,
            edited: false,
            menuItemAppearances: getMenuItemAppearanceGroupState(dialogData.diningStationTemplate.menuItemAppearances, openDiningStationTemplateForDuplicateDialog ? "create" : "edit"),
          }}
        />
      )}
      {createDialogOpen && (
        <Dialog
          open={createDialogOpen}
          type={"create"}
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleSave}
          saving={saving}
          initialDiningStationTemplate={getNewDiningStationTemplateModalState()}
        />
      )}
      {diningStationTemplateForDeleteDialog !== null && (
        <ConfirmationDialog
          open={diningStationTemplateForDeleteDialog !== null}
          title="Delete Dining Station Template"
          message="Are you sure that you would like to delete the dining station template?"
          onCancel={() => setDiningStationTemplateForDeleteDialog(null)}
          onConfirm={() => handleDelete(diningStationTemplateForDeleteDialog.id)}
          variant="containedDestructive"
        />
      )}
    </>
  );
};
